import { Dispatch, SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { AxiosError, AxiosResponse } from 'axios';
import { useSubscription } from '@apollo/client';
import { useBackdropContext } from '../../../../../../context/backdrop';
import { useInstallationContext } from '../../../../../../context/installation';
import {
  InputTriggerInternal,
  OnDeviceErrorSubscription,
  OnDeviceErrorSubscriptionVariables,
} from '../../../../../../data-access/gql-types/graphql';
import { ON_DEVICE_ERROR } from '../../../../../../data-access/subscriptions/lavva-devices';
import { useErrors } from '../../../../../../hooks/errors/use-errors';
import { InputTriggerInternalRequest } from '../../../../types/graphql-rest-converter';
import { timeToMilliseconds } from '../../../../../../utils/helpers';
import { toastError, toastSuccess } from '../../../../../../utils/toast';
import { useSetActionBindings } from '../../../../api/device/device.hooks';
import { DeviceBindingsBody } from '../../../../api/device/device.types';
import { ActionBindingType, BindingError, BindingErrorItem, DeviceBindingsForm } from '../types';
import { getEnumKeySlot } from '../utils';

export const useSubmitBindings = () => {
  const { t } = useTranslation('device-settings');
  const { t: tb } = useTranslation('backend-validation');
  const { deviceId } = useParams<{ deviceId: string }>();
  const { skipLavvaFetch, selectedInstallationId } = useInstallationContext();
  const { mutate } = useSetActionBindings();
  const { handleLavvaResolve, handleRestErrors } = useErrors();
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { data: deviceErrorData } = useSubscription<OnDeviceErrorSubscription, OnDeviceErrorSubscriptionVariables>(
    ON_DEVICE_ERROR,
    {
      variables: { installationId: selectedInstallationId },
      skip: skipLavvaFetch,
    },
  );

  useEffect(() => {
    if (deviceErrorData?.onDeviceError) {
      toastError({ content: tb(deviceErrorData.onDeviceError.code) });
    }
  }, [deviceErrorData]);

  const handleSubmitBindings = (
    values: DeviceBindingsForm,
    setErrors: Dispatch<SetStateAction<Record<string, string[]>>>,
  ) => {
    if (!deviceId) return;
    turnOnBackdrop();
    setErrors({});

    const request: DeviceBindingsBody = {
      deviceId,
      bindings: values.bindings.map((x) => ({
        inputId: x.inputId,
        bindings: [...x.single, ...x.double]
          .filter((b) => b.slot !== null)
          .map((y) => {
            const slot = getEnumKeySlot(y.channelType, y.slot);

            return {
              $: ActionBindingType[y.channelType.charAt(0).toUpperCase() + y.channelType.slice(1).toLowerCase()],
              inputTrigger:
                y.inputTrigger !== null
                  ? InputTriggerInternalRequest[y.inputTrigger]
                  : InputTriggerInternalRequest[InputTriggerInternal.SingleClick],
              ...(slot ? { slot } : {}),
              isDefault: y.actionType
                ? ![
                    ActionBindingType.Switch,
                    ActionBindingType.Light,
                    ActionBindingType.Blind,
                    ActionBindingType.Gate,
                    ActionBindingType.Cloud,
                  ].includes(y.actionType)
                : true,
              ...(y.channelId !== 'cloud'
                ? {
                    destination: {
                      channelId: y.channelId,
                      deviceId,
                    },
                  }
                : {}),
              ...(y.setOnTimeValue !== undefined ? { setOnTimeValue: timeToMilliseconds(y.setOnTimeValue) } : {}),
              ...(y.position !== undefined ? { position: y.position } : {}),
              ...(y.dir !== undefined ? { dir: y.dir } : {}),
              ...(y.temperature !== undefined ? { temperature: y.temperature } : {}),
              ...(y.brightness !== undefined ? { brightness: y.brightness } : {}),
              ...(y.r !== undefined ? { r: y.r } : {}),
              ...(y.g !== undefined ? { g: y.g } : {}),
              ...(y.b !== undefined ? { b: y.b } : {}),
              ...(y.onSignalActionId !== undefined && y.onSignalActionId !== null
                ? { onSignalActionId: y.onSignalActionId }
                : {}),
              ...(y.offSignalActionId !== undefined && y.offSignalActionId !== null
                ? { offSignalActionId: y.offSignalActionId }
                : {}),
            };
          }),
      })),
    };

    mutate(request, {
      onSuccess: ({ data }) => {
        handleLavvaResolve({
          status: data.publishStatus,
          deviceId,
          onSuccess: () => {
            turnOffBackdrop();
            toastSuccess({ content: t('bindings.submitResult.success') });
          },
        });
      },
      onError: (error: AxiosError) => {
        turnOffBackdrop();
        const response = error.response;

        if (response) {
          if ('errors' in (response as AxiosResponse).data) {
            const errorList = (response as AxiosResponse).data.errors as BindingErrorItem[];

            errorList.map((x) => {
              const errorParsed = JSON.parse(x.errorMessage) as BindingError;
              const errorCode = errorParsed.ErrorCode;
              errorParsed.FormattedMessagePlaceholderValues.PropertyValue.Bindings.map((binding) => {
                setErrors((prev) => {
                  const prevErrors = prev[binding.InputId] || [];

                  return {
                    ...prev,
                    [binding.InputId]: [...prevErrors, tb(errorCode)],
                  };
                });
              });
            });
          }
        }

        handleRestErrors(error, t('bindings.submitResult.error'));
      },
    });
  };

  return {
    handleSubmitBindings,
  };
};
